// Screen 14 — Wallet provisioning (Phase 8 crypto, ADR-0033).
//
// PRODUCT MODEL: the admin pastes a deposit ADDRESS the operator already
// controls (a TRON T-address or an ETH 0x-address) and ASSIGNS it to a client.
// There is NO HD/xpub derivation here and NO signing key is ever created or held
// by this portal — the browser holds no credentials. The backend checksum-
// validates + canonicalizes the pasted address, stores it on the
// (chain, address) unique index, and a server-side listener then watches that
// address and credits any deposit to the assigned client's ledger.
//
// SECURITY: this UI never sees the master TikTok token or any backend secret.
// Provisioning writes go ONLY to the authorized admin endpoint
// POST /admin/clients/{id}/wallets via the same JWT-attaching api client every
// other admin screen uses. We validate the address format client-side purely to
// fail fast with a friendly message — the backend is the source of truth and
// re-validates (defense in depth).
//
// DEPOSITS: actual on-chain deposits land in the Master Ledger (a credit entry
// per confirmed deposit); this screen is only the address-assignment surface, so
// there is no deposits table here — just a short pointer to the ledger.

import { useEffect, useState } from "react";
import { CheckIcon, CopyIcon } from "lucide-react";
import { toast } from "sonner";

import { api } from "@/api/client";
import {
  getUnmatchedDeposits,
  type UnmatchedDeposit,
  type UnmatchedDepositReason,
} from "@/api/deposits";
import { errMessage } from "@/lib/errMessage";
import { cn } from "@/lib/utils";
import { AdminShell } from "@/components/nexus/AdminShell";
import {
  AddressBlock,
  DataTable,
  ToneBadge,
  type Column,
  type Tone,
} from "@/components/nexus";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { NativeSelect, Segmented } from "./controls";

// The chain wire value matches internal/wallets.Chain ("tron" | "eth") — the
// exact string the backend's assignWalletRequest expects.
export type Chain = "tron" | "eth";

// A client as returned by GET /admin/clients (same shape ClientsPage consumes).
interface Client {
  id: string;
  name: string;
  email: string;
  enabled: boolean;
  created_at: number;
}

// One assigned wallet as returned by the admin wallets endpoints. The live
// backend (provisionedWalletJSON) returns only {chain, address}; created_at is
// OPTIONAL so the "Added" column renders a date when the backend provides one
// (and the MSW mock does) and degrades to "—" otherwise.
interface AssignedWallet {
  chain: Chain;
  address: string;
  created_at?: number; // Unix seconds, when present.
}

// ---- Client-side address validation -----------------------------------------
//
// Pure, exported so it is unit-testable in isolation. This is a FAST-FAIL format
// gate only — the backend re-validates the checksum (TRON Base58Check / ETH
// EIP-55) and is the source of truth.

const TRON_RE = /^T[1-9A-HJ-NP-Za-km-z]{33}$/; // 'T' + 33 Base58 chars (~34 total)
const ETH_RE = /^0x[0-9a-fA-F]{40}$/; // '0x' + exactly 40 hex chars

export function validateAddress(chain: Chain, raw: string): string | null {
  const addr = raw.trim();
  if (addr === "") return "Paste a deposit address.";
  if (chain === "tron") {
    if (!addr.startsWith("T")) {
      return "A TRON address must start with 'T'.";
    }
    if (!TRON_RE.test(addr)) {
      return "Not a valid TRON address (expected 'T' + 33 Base58 characters).";
    }
    return null;
  }
  // eth
  if (!addr.startsWith("0x") && !addr.startsWith("0X")) {
    return "An ETH address must start with '0x'.";
  }
  if (!ETH_RE.test(addr)) {
    return "Not a valid ETH address (expected '0x' + 40 hex characters).";
  }
  return null;
}

// FullAddress — the full monospace address + a copy button for the assigned-
// wallets table. Unlike the elided AddressBlock, the admin needs to verify the
// EXACT address it pasted, so the full string is shown.
function FullAddress({ address }: { address: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard?.writeText(address);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      // Clipboard may be unavailable (insecure context); ignore silently.
    }
  }
  return (
    <span className="inline-flex items-center gap-1.5">
      <code className="font-mono text-xs break-all text-nx-text-2 tabular-nums">
        {address}
      </code>
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        onClick={copy}
        aria-label={copied ? "Copied" : "Copy address"}
      >
        {copied ? <CheckIcon className="text-nx-credit" /> : <CopyIcon />}
      </Button>
    </span>
  );
}

function chainLabel(chain: Chain): string {
  return chain === "tron" ? "TRON" : "ETH";
}

// Tolerant chain label for the unmatched-deposits panel, where `chain` is a
// plain string (an unexpected chain uppercases rather than crashing).
function chainLabelOf(chain: string): string {
  if (chain === "tron") return "TRON";
  if (chain === "eth") return "ETH";
  return chain.toUpperCase() || "—";
}

// The reason a matched deposit was NOT credited: "unmatched" is real money-in
// that hit no provisioned address (needs manual resolution → warn tone);
// "skipped_predates" arrived before its address's provisioning window
// (informational → na tone). An unexpected value renders neutrally.
function reasonTone(reason: UnmatchedDepositReason | string): Tone {
  if (reason === "unmatched") return "warn";
  if (reason === "skipped_predates") return "na";
  return "na";
}

function reasonLabel(reason: UnmatchedDepositReason | string): string {
  return reason === "skipped_predates" ? "skipped (predates)" : reason;
}

// Middle-elide a long hash for a compact monospace cell; the full value is on
// the title attribute so it stays inspectable on hover.
function truncHash(value: string): string {
  if (value.length <= 18) return value;
  return `${value.slice(0, 10)}…${value.slice(-6)}`;
}

export function WalletsPage() {
  // Clients list (reused from the same /admin/clients fetch the Clients screen
  // uses) to drive the picker.
  const [clients, setClients] = useState<Client[]>([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [clientsError, setClientsError] = useState<string | null>(null);

  // Form state.
  const [clientId, setClientId] = useState("");
  const [chain, setChain] = useState<Chain>("tron");
  const [address, setAddress] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // The selected client's currently-assigned wallets.
  const [wallets, setWallets] = useState<AssignedWallet[]>([]);
  const [walletsLoading, setWalletsLoading] = useState(false);
  const [walletsError, setWalletsError] = useState<string | null>(null);

  // Unmatched deposits (GLOBAL — not scoped to the selected client). Real
  // money-in the listener saw land on a non-provisioned address.
  const [deposits, setDeposits] = useState<UnmatchedDeposit[]>([]);
  const [depositsLoading, setDepositsLoading] = useState(true);
  const [depositsError, setDepositsError] = useState<string | null>(null);

  async function loadClients() {
    setClientsLoading(true);
    setClientsError(null);
    try {
      const res = await api.get<{ clients: Client[] }>("/admin/clients");
      setClients(res.clients);
    } catch (err) {
      setClientsError(errMessage(err));
    } finally {
      setClientsLoading(false);
    }
  }

  async function loadWallets(id: string) {
    if (!id) {
      setWallets([]);
      return;
    }
    setWalletsLoading(true);
    setWalletsError(null);
    try {
      const res = await api.get<{ wallets: AssignedWallet[] }>(
        `/admin/clients/${id}/wallets`,
      );
      setWallets(res.wallets);
    } catch (err) {
      setWalletsError(errMessage(err));
      setWallets([]);
    } finally {
      setWalletsLoading(false);
    }
  }

  async function loadDeposits() {
    setDepositsLoading(true);
    setDepositsError(null);
    try {
      const res = await getUnmatchedDeposits();
      setDeposits(res.deposits ?? []);
    } catch (err) {
      setDepositsError(errMessage(err));
      setDeposits([]);
    } finally {
      setDepositsLoading(false);
    }
  }

  useEffect(() => {
    void loadClients();
    void loadDeposits();
  }, []);

  // Re-load the assigned-wallets table whenever the selected client changes.
  useEffect(() => {
    void loadWallets(clientId);
  }, [clientId]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);

    if (!clientId) {
      setFormError("Select a client first.");
      return;
    }
    // Client-side format gate (fast-fail; the backend re-validates the checksum).
    const vErr = validateAddress(chain, address);
    if (vErr) {
      setFormError(vErr);
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post<{ wallets: AssignedWallet[] }>(
        `/admin/clients/${clientId}/wallets`,
        { chain, address: address.trim() },
      );
      // 201 — clear the form, refresh the list from the authoritative response,
      // and confirm.
      setWallets(res.wallets);
      setAddress("");
      toast.success(`${chainLabel(chain)} address assigned`);
    } catch (err) {
      const status = (err as { status?: number })?.status;
      if (status === 409) {
        setFormError("This address is already assigned to a client.");
      } else if (status === 400) {
        setFormError(
          "The backend rejected this address (invalid or failed checksum).",
        );
      } else {
        setFormError(errMessage(err));
      }
    } finally {
      setSubmitting(false);
    }
  }

  const selectedClient = clients.find((c) => c.id === clientId);

  const columns: Column<AssignedWallet>[] = [
    {
      key: "chain",
      header: "Chain",
      cell: (w) => <ToneBadge tone="brand">{chainLabel(w.chain)}</ToneBadge>,
    },
    {
      key: "address",
      header: "Deposit address",
      cell: (w) => <FullAddress address={w.address} />,
    },
    {
      key: "added",
      header: "Added",
      align: "right",
      cell: (w) => (
        <span className="font-mono text-xs text-nx-text-3 tabular-nums">
          {w.created_at
            ? new Date(w.created_at * 1000).toLocaleDateString()
            : "—"}
        </span>
      ),
    },
  ];

  const depositColumns: Column<UnmatchedDeposit>[] = [
    {
      key: "chain",
      header: "Chain",
      cell: (d) => <ToneBadge tone="brand">{chainLabelOf(d.chain)}</ToneBadge>,
    },
    {
      key: "address",
      header: "Address",
      cell: (d) => <AddressBlock address={d.address} />,
    },
    {
      key: "amount",
      header: "Amount",
      align: "right",
      cell: (d) => (
        <span className="font-mono text-sm text-nx-text tabular-nums">
          {d.amount}
        </span>
      ),
    },
    {
      key: "tx",
      header: "Tx hash",
      cell: (d) => (
        <code
          className="font-mono text-xs text-nx-text-2 tabular-nums"
          title={d.tx_hash}
        >
          {truncHash(d.tx_hash)}
        </code>
      ),
    },
    {
      key: "reason",
      header: "Reason",
      cell: (d) => (
        <ToneBadge tone={reasonTone(d.reason)}>
          {reasonLabel(d.reason)}
        </ToneBadge>
      ),
    },
    {
      key: "seen",
      header: "Seen",
      align: "right",
      cell: (d) => (
        <span className="font-mono text-xs text-nx-text-3 tabular-nums">
          {d.seen_at ? new Date(d.seen_at).toLocaleString() : "—"}
        </span>
      ),
    },
  ];

  return (
    <AdminShell>
      <div className="mb-5">
        <h2 className="font-heading text-lg font-semibold">
          Wallet provisioning
        </h2>
        <p className="mt-1 max-w-2xl text-sm text-nx-text-2">
          Assign a crypto deposit address the operator controls to a client. The
          system watches that address and credits confirmed deposits to the
          client&apos;s ledger. No signing key is ever created or held by this
          portal.
        </p>
      </div>

      {clientsError && (
        <Alert variant="destructive" className="mb-4">
          <AlertTitle>Could not load clients</AlertTitle>
          <AlertDescription>{clientsError}</AlertDescription>
        </Alert>
      )}

      <div className="grid gap-5 lg:grid-cols-[22rem_1fr]">
        <Card className="h-fit gap-4 p-5">
          <form onSubmit={onSubmit} className="grid gap-4" noValidate>
            <div>
              <label
                htmlFor="wallet-client"
                className="mb-1 block text-xs font-medium tracking-wide text-nx-text-2 uppercase"
              >
                Client
              </label>
              <NativeSelect
                id="wallet-client"
                value={clientId}
                disabled={clientsLoading}
                onChange={(e) => {
                  setClientId(e.target.value);
                  setFormError(null);
                }}
              >
                <option value="">
                  {clientsLoading ? "Loading clients…" : "Select a client…"}
                </option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </NativeSelect>
            </div>

            <div>
              <span className="mb-1 block text-xs font-medium tracking-wide text-nx-text-2 uppercase">
                Chain
              </span>
              <Segmented<Chain>
                value={chain}
                onChange={(c) => {
                  setChain(c);
                  setFormError(null);
                }}
                options={[
                  { value: "tron", label: "TRON" },
                  { value: "eth", label: "ETH" },
                ]}
              />
            </div>

            <div>
              <label
                htmlFor="wallet-address"
                className="mb-1 block text-xs font-medium tracking-wide text-nx-text-2 uppercase"
              >
                Deposit address
              </label>
              <Input
                id="wallet-address"
                value={address}
                spellCheck={false}
                autoComplete="off"
                aria-invalid={formError != null}
                placeholder={
                  chain === "tron" ? "T…" : "0x…"
                }
                className="font-mono"
                onChange={(e) => {
                  setAddress(e.target.value);
                  if (formError) setFormError(null);
                }}
              />
              <p className="mt-1 text-xs text-nx-text-3">
                {chain === "tron"
                  ? "TRON: starts with 'T', Base58, ~34 characters."
                  : "ETH: '0x' followed by exactly 40 hex characters."}
              </p>
            </div>

            {formError && (
              <p
                role="alert"
                className={cn(
                  "rounded-md bg-nx-danger-soft px-3 py-2 text-xs font-medium text-nx-danger",
                )}
              >
                {formError}
              </p>
            )}

            <Button
              type="submit"
              disabled={submitting || clientsLoading}
              className="w-full"
            >
              {submitting ? "Assigning…" : "Assign address"}
            </Button>

            <p className="rounded-md bg-nx-bg-2 px-3 py-2 text-xs text-nx-text-3">
              Watch-only · the operator controls this address · no signing key
              ever created or held here.
            </p>
          </form>
        </Card>

        <div>
          <div className="mb-2 flex items-baseline justify-between">
            <h3 className="text-xs font-medium tracking-wide text-nx-text-2 uppercase">
              Assigned addresses
            </h3>
            {selectedClient && (
              <span className="text-xs text-nx-text-3">
                {selectedClient.name}
              </span>
            )}
          </div>

          {!clientId ? (
            <p className="py-8 text-center text-sm text-nx-text-3">
              Select a client to see its assigned deposit addresses.
            </p>
          ) : walletsError ? (
            <Alert variant="destructive">
              <AlertTitle>Could not load assigned addresses</AlertTitle>
              <AlertDescription>{walletsError}</AlertDescription>
            </Alert>
          ) : walletsLoading ? (
            <div className="grid gap-2">
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
            </div>
          ) : (
            <DataTable
              columns={columns}
              rows={wallets}
              rowKey={(w) => `${w.chain}:${w.address}`}
              empty="No deposit addresses assigned to this client yet."
            />
          )}

          <p className="mt-3 text-xs text-nx-text-3">
            Confirmed deposits to these addresses are credited to the client in
            the{" "}
            <span className="font-medium text-nx-text-2">Master Ledger</span> —
            this screen only assigns the watched address.
          </p>
        </div>
      </div>

      {/* Unmatched deposits — GLOBAL. Real money-in the listener saw land on an
          address that matched no provisioned client wallet (or that predates
          its provisioning window). Read-only; ops must resolve it manually. */}
      <div className="mt-8">
        <div className="mb-2 flex items-baseline justify-between gap-3">
          <div>
            <h3 className="font-heading text-base font-semibold">
              Unmatched deposits
            </h3>
            <p className="mt-0.5 max-w-2xl text-sm text-nx-text-2">
              Incoming deposits that matched no provisioned address (or arrived
              before their address's provisioning window). This is real money-in
              that hit no client — resolve each one manually.
            </p>
          </div>
          {!depositsLoading && !depositsError && deposits.length > 0 && (
            <ToneBadge tone="warn" className="shrink-0">
              {deposits.length} unresolved
            </ToneBadge>
          )}
        </div>

        {depositsError ? (
          <Alert variant="destructive">
            <AlertTitle>Could not load unmatched deposits</AlertTitle>
            <AlertDescription>{depositsError}</AlertDescription>
          </Alert>
        ) : depositsLoading ? (
          <div className="grid gap-2">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        ) : (
          <DataTable
            columns={depositColumns}
            rows={deposits}
            rowKey={(d) => d.id}
            empty="No unmatched deposits — all incoming deposits matched a provisioned address."
          />
        )}
      </div>
    </AdminShell>
  );
}
